import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface UIStore {
  sidebarCollapsed: boolean;
  filterSidebarCollapsed: boolean;
  showFilterSidebar: boolean;
  dateTimePickerOpen: boolean;
  expandedFilterIds: string[];
  focusedFilterId: string | null;
  appliedFiltersExpanded: boolean;
  locateColumnRequest: { columnId: string; trigger: number } | null;
  /** Column IDs to highlight in table (set when user clicks Locate in filter sidebar only). */
  locateHighlightedColumnIds: string[] | null;
  /** Column ID to briefly highlight in the sidebar Columns tab (set after pin/hide via context menu). */
  sidebarHighlightColumnId: string | null;
  columnsTabActive: boolean;
  sidebarActiveTab: 'filters' | 'columns';
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleFilterSidebar: () => void;
  setFilterSidebarCollapsed: (collapsed: boolean) => void;
  setShowFilterSidebar: (show: boolean) => void;
  setDateTimePickerOpen: (open: boolean) => void;
  setExpandedFilterIds: (ids: string[]) => void;
  expandFilter: (filterId: string) => void;
  collapseFilter: (filterId: string) => void;
  expandAllFilters: (allFilterIds: string[]) => void;
  collapseAllFilters: () => void;
  collapseInactiveFilters: (activeFilterIds: string[]) => void;
  setFocusedFilterId: (filterId: string | null) => void;
  setAppliedFiltersExpanded: (expanded: boolean) => void;
  setLocateColumnRequest: (req: { columnId: string; trigger: number } | null) => void;
  setLocateHighlightedColumnIds: (ids: string[] | null) => void;
  setSidebarHighlightColumnId: (id: string | null) => void;
  setColumnsTabActive: (active: boolean) => void;
  setSidebarActiveTab: (tab: 'filters' | 'columns') => void;
}

export const useUIStore = create<UIStore>()(
  devtools(
    (set) => ({
      sidebarCollapsed: true,
      filterSidebarCollapsed: true,
      showFilterSidebar: true,
      dateTimePickerOpen: false,
      expandedFilterIds: [],
      focusedFilterId: null,
      appliedFiltersExpanded: false,
      locateColumnRequest: null,
      locateHighlightedColumnIds: null,
      sidebarHighlightColumnId: null,
      columnsTabActive: false,
      sidebarActiveTab: 'filters',
      toggleSidebar: () => set((state) => ({
        sidebarCollapsed: !state.sidebarCollapsed
      })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleFilterSidebar: () => set((state) => ({
        filterSidebarCollapsed: !state.filterSidebarCollapsed
      })),
      setFilterSidebarCollapsed: (collapsed) => set({ filterSidebarCollapsed: collapsed }),
      setShowFilterSidebar: (show) => set({ showFilterSidebar: show }),
      setDateTimePickerOpen: (open) => set({ dateTimePickerOpen: open }),
      setExpandedFilterIds: (ids) => set({ expandedFilterIds: ids }),
      expandFilter: (filterId) =>
        set((state) => ({
          expandedFilterIds: state.expandedFilterIds.includes(filterId)
            ? state.expandedFilterIds
            : [...state.expandedFilterIds, filterId],
        })),
      collapseFilter: (filterId) =>
        set((state) => ({
          expandedFilterIds: state.expandedFilterIds.filter((id) => id !== filterId),
        })),
      expandAllFilters: (allFilterIds) =>
        set({ expandedFilterIds: [...allFilterIds] }),
      collapseAllFilters: () => set({ expandedFilterIds: [] }),
      collapseInactiveFilters: (activeFilterIds) =>
        set((state) => ({
          expandedFilterIds: state.expandedFilterIds.filter((id) =>
            activeFilterIds.includes(id)
          ),
        })),
      setFocusedFilterId: (filterId) => set({ focusedFilterId: filterId }),
      setAppliedFiltersExpanded: (expanded) => set({ appliedFiltersExpanded: expanded }),
      setLocateColumnRequest: (req) => set({ locateColumnRequest: req }),
      setLocateHighlightedColumnIds: (ids) => set({ locateHighlightedColumnIds: ids }),
      setSidebarHighlightColumnId: (id) => set({ sidebarHighlightColumnId: id }),
      setColumnsTabActive: (active) => set({ columnsTabActive: active }),
      setSidebarActiveTab: (tab) => set({ sidebarActiveTab: tab }),
    }),
    {
      name: 'ui-store', enabled: import.meta.env.DEV,
    }
  )
);

